import * as React from "react";
import { Dayjs } from "dayjs";
import TextField from "@mui/material/TextField";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import "./_style.scss";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";

export default function BasicTimePicker({
  formik,
  className,
  errorStart,
  errorEnd,
  nameStart,
  nameEnd
}) {
  const START = formik?.getFieldProps(nameStart);
  const END = formik?.getFieldProps(nameEnd);

  // State to track if pickers are open
  const [isStartPickerOpen, setIsStartPickerOpen] = React.useState(false);
  const [isEndPickerOpen, setIsEndPickerOpen] = React.useState(false);

  // Refs for the picker containers
  const startPickerRef = React.useRef(null);
  const endPickerRef = React.useRef(null);

  // Handle time change with validation
  const handleTimeChange = (fieldName, newValue) => {
    if (newValue && newValue.$d) {
      formik.setFieldValue(fieldName, newValue.$d);
    }
  };

  // Handle picker open/close with proper cleanup
  const handleStartPickerOpen = () => {
    setIsStartPickerOpen(true);
    // Close end picker if it's open
    if (isEndPickerOpen) {
      setIsEndPickerOpen(false);
    }
  };

  const handleEndPickerOpen = () => {
    setIsEndPickerOpen(true);
    // Close start picker if it's open
    if (isStartPickerOpen) {
      setIsStartPickerOpen(false);
    }
  };

  const handleStartPickerClose = () => {
    setIsStartPickerOpen(false);
  };

  const handleEndPickerClose = () => {
    setIsEndPickerOpen(false);
  };

  // Prevent clicks on invisible areas only when pickers are closed
  React.useEffect(() => {
    const handleGlobalClick = (e) => {
      const target = e.target;
      
      // Only prevent interactions when both pickers are closed
      if (!isStartPickerOpen && !isEndPickerOpen) {
        // Check if the target is a hidden picker element
        if (target.classList.contains('MuiPickersPopper-root') || 
            target.closest('.MuiPickersPopper-root')) {
          
          const pickerElement = target.classList.contains('MuiPickersPopper-root') ? 
                               target : target.closest('.MuiPickersPopper-root');
          
          if (pickerElement && (
              pickerElement.style.display === 'none' || 
              pickerElement.style.visibility === 'hidden' ||
              pickerElement.getAttribute('aria-hidden') === 'true' ||
              pickerElement.style.opacity === '0'
          )) {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }
        }
      }
    };

    const handleGlobalMouseDown = (e) => {
      const target = e.target;
      
      // Only prevent interactions when both pickers are closed
      if (!isStartPickerOpen && !isEndPickerOpen) {
        if (target.classList.contains('MuiPickersPopper-root') || 
            target.closest('.MuiPickersPopper-root')) {
          
          const pickerElement = target.classList.contains('MuiPickersPopper-root') ? 
                               target : target.closest('.MuiPickersPopper-root');
          
          if (pickerElement && (
              pickerElement.style.display === 'none' || 
              pickerElement.style.visibility === 'hidden' ||
              pickerElement.getAttribute('aria-hidden') === 'true' ||
              pickerElement.style.opacity === '0'
          )) {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }
        }
      }
    };

    // Add event listeners with capture phase
    document.addEventListener('click', handleGlobalClick, true);
    document.addEventListener('mousedown', handleGlobalMouseDown, true);
    
    return () => {
      // Cleanup event listeners
      document.removeEventListener('click', handleGlobalClick, true);
      document.removeEventListener('mousedown', handleGlobalMouseDown, true);
    };
  }, [isStartPickerOpen, isEndPickerOpen]);

  // Cleanup function for component unmount
  React.useEffect(() => {
    return () => {
      // Ensure pickers are closed when component unmounts
      setIsStartPickerOpen(false);
      setIsEndPickerOpen(false);
    };
  }, []);

  // Force cleanup of any remaining picker elements
  React.useEffect(() => {
    const cleanupHiddenPickers = () => {
      // Find and disable any hidden picker elements
      const hiddenPickers = document.querySelectorAll('.MuiPickersPopper-root');
      hiddenPickers.forEach(picker => {
        if (picker.style.display === 'none' || 
            picker.style.visibility === 'hidden' ||
            picker.getAttribute('aria-hidden') === 'true' ||
            picker.style.opacity === '0') {
          picker.style.pointerEvents = 'none';
          picker.style.display = 'none';
        }
      });
    };

    // Run cleanup periodically
    const interval = setInterval(cleanupHiddenPickers, 100);
    
    return () => {
      clearInterval(interval);
    };
  }, []);

  return (
    <div className={`TimePicker_Wrapper`}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <div className={className} ref={startPickerRef}>
          <span> From </span>
          <TimePicker
            value={START?.value}
            onChange={(e) => handleTimeChange(nameStart, e)}
            onOpen={handleStartPickerOpen}
            onClose={handleStartPickerClose}
            open={isStartPickerOpen}
            renderInput={({ inputProps, ...restParams }) => (
              <TextField
                {...restParams}
                error={false}
                inputProps={{
                  ...inputProps,
                  placeholder: "00 : 00",
                  readOnly: true, // Prevent direct input modification
                }}
                style={{ width: '100%' }}
                onClick={handleStartPickerOpen}
              />
            )}
            PopperProps={{
              style: {
                zIndex: 9999,
              },
              onClick: (e) => {
                // Prevent clicks on the popper from bubbling up
                e.stopPropagation();
              },
              onMouseDown: (e) => {
                // Prevent mouse down events from interfering
                e.stopPropagation();
              },
              // Force proper positioning and prevent overlay issues
              placement: "bottom-start",
              disablePortal: false,
            }}
          />
          {errorStart ? (
            <p className="error">
              <ReportProblemIcon /> {errorStart}
            </p>
          ) : null}
        </div>
        <div className={`${className}  endtime_wrapper`} ref={endPickerRef}>
          <span>To</span>
          <TimePicker
            value={END?.value}
            onChange={(e) => handleTimeChange(nameEnd, e)}
            onOpen={handleEndPickerOpen}
            onClose={handleEndPickerClose}
            open={isEndPickerOpen}
            renderInput={({ inputProps, ...restParams }) => (
              <TextField
                {...restParams}
                error={false}
                inputProps={{
                  ...inputProps,
                  placeholder: "00 : 00",
                  readOnly: true, // Prevent direct input modification
                }}
                style={{ width: '100%' }}
                onClick={handleEndPickerOpen}
              />
            )}
            PopperProps={{
              style: {
                zIndex: 9999,
              },
              onClick: (e) => {
                // Prevent clicks on the popper from bubbling up
                e.stopPropagation();
              },
              onMouseDown: (e) => {
                // Prevent mouse down events from interfering
                e.stopPropagation();
              },
              // Force proper positioning and prevent overlay issues
              placement: "bottom-start",
              disablePortal: false,
            }}
          />
          {errorEnd ? (
            <p className="error">
              <ReportProblemIcon /> {errorEnd}
            </p>
          ) : null}
        </div>
      </LocalizationProvider>
    </div>
  );
}

